'use client';

// RealtimeRegistrationsProvider — the consumer contract for the admin
// auction-registrations stream (BL-1262). Sibling of `RealtimeLotsProvider`.
//
// Owns the RegistrationStateStore (subscription, LWW-per-registration fold,
// cursor, cold-seed + reconcile catch-up) and the wake/reconnect triggers;
// consumers read `useRegistrations()` and hold NONE of that plumbing.
//
// The transport lives entirely behind this provider, so the eventual move off
// Supabase Realtime (AlloyDB / authenticated SSE — a separate project) is an
// adapter swap with zero consumer changes.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

import type { AdminRegistrationDelta } from '../../mappers/realtime/admin-auction-registrations';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { installReconcileTriggers } from '../../stores/realtime/install-reconcile-triggers';
import {
    DISCONNECTED_REGISTRATION_STATE,
    RegistrationStateStore,
    type RegistrationStateCatchupFetcher,
    type RegistrationStateConnection,
} from '../../stores/realtime/registration-state-store';
import type { NotifyScheduler } from '../../stores/realtime/entity-state-store';

/**
 * No timer (AC 5). BL-1630 gave this stream a dense per-auction sequence, so a dropped
 * delta is DETECTED as a hole on the next one and recovered by a targeted catch-up on
 * `sequence > held` — which is cheaper than a blind poll on a quiet socket, and faster
 * than a timer when a message is genuinely lost. The 30 s value this replaced predated
 * detection and could only ask "did I miss anything?" of a stream that had no way to
 * answer.
 *
 * The other four reconcile triggers are UNCHANGED (RAC 4): focus, visibilitychange,
 * online, and transport reconnect all still force a sweep. `installReconcileTriggers`
 * reads `0` as "install no interval" and leaves those listeners alone.
 *
 * Do NOT restore a value here as a "safety net" — a timer on a gap-detecting stream is
 * pure duplicate load. All three entity-state streams now sit at 0.
 */
const RECONCILE_INTERVAL_MS = 0;

/** Stable empty map for SSR / no-provider server snapshots (ref-stable). */
const EMPTY_REGISTRATIONS: ReadonlyMap<string, AdminRegistrationDelta> = new Map();

const RegistrationStateStoreContext = createContext<RegistrationStateStore | null>(null);

export interface RealtimeRegistrationsProviderProps {
    /** The auction whose registrations stream to fold. */
    auctionId: string;
    /** Transport subscriber (shared with the app's RealtimeStack). */
    subscriber: RealtimeSubscriber;
    /** Authenticated catch-up fetcher (mints the topic + cold-seed). */
    catchupFetcher: RegistrationStateCatchupFetcher;
    /** Override the reconcile interval (tests pass `0` to disable). */
    reconcileIntervalMs?: number;
    /**
     * Override the coalesced-notify scheduler (BUG-1407). Same test-seam role as
     * `reconcileIntervalMs`: production takes the default short-timer window, and
     * a test injects a manual pump so "one render per burst" is an exact
     * assertion rather than a race against a timer.
     */
    scheduleNotify?: NotifyScheduler;
    reconcileThrottleMs?: number;
    children: ReactNode;
}

/**
 * Mount around any subtree that renders admin registration state for one
 * auction. Owns the store; `useRegistrations` reads it.
 */
export function RealtimeRegistrationsProvider({
    auctionId,
    subscriber,
    catchupFetcher,
    reconcileIntervalMs = RECONCILE_INTERVAL_MS,
    reconcileThrottleMs,
    scheduleNotify,
    children,
}: RealtimeRegistrationsProviderProps) {
    const store = useMemo(
        () => new RegistrationStateStore(subscriber, catchupFetcher, auctionId, scheduleNotify),
        [subscriber, catchupFetcher, auctionId, scheduleNotify],
    );

    useEffect(() => {
        void store.start();
        return () => store.stop();
    }, [store]);

    useEffect(() => {
        return installReconcileTriggers({
            reconcileAll: () => store.reconcile(),
            subscriber,
            intervalMs: reconcileIntervalMs,
            reconcileThrottleMs,
        });
    }, [store, subscriber, reconcileIntervalMs, reconcileThrottleMs]);

    return (
        <RegistrationStateStoreContext.Provider value={store}>
            {children}
        </RegistrationStateStoreContext.Provider>
    );
}

/**
 * The registrations store when a `<RealtimeRegistrationsProvider>` is above,
 * else `null` — for a consumer that is optional-by-design off the clerk
 * console (BL-1546: the bid record mounts on the Items row, the lot editor and
 * the Post-auction slide-out, none of which own an auction-wide registrations
 * stream). Same shape as `useOptionalLotStateStore`; `useRegistrations` stays
 * strict for consumers that genuinely require the stream.
 *
 * @returns the auction's registration store, or `null` off-console.
 */
export function useOptionalRegistrationStore(): RegistrationStateStore | null {
    return useContext(RegistrationStateStoreContext);
}

function useRegistrationStateStore(): RegistrationStateStore {
    const store = useContext(RegistrationStateStoreContext);
    if (!store) {
        throw new Error(
            'useRegistrations must be used inside a <RealtimeRegistrationsProvider>',
        );
    }
    return store;
}

/** Read the folded registration map (the `useSyncExternalStore` seam). */
function useRegistrationMap(): ReadonlyMap<string, AdminRegistrationDelta> {
    const store = useRegistrationStateStore();
    const subscribe = useCallback(
        (listener: () => void) => store.subscribe(listener),
        [store],
    );
    const getSnapshot = useCallback(() => store.getRegistrations(), [store]);
    return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_REGISTRATIONS);
}

export interface UseRegistrationsOptions<T> {
    /**
     * Project each registration to only the slice the consumer renders. Wrap in
     * `useCallback` to avoid recomputing every render. Omit to get the full
     * `AdminRegistrationDelta`.
     */
    select?: (registration: AdminRegistrationDelta) => T;
    /** Sort comparator over the raw deltas (e.g. pending-first by requestedAt). */
    sortBy?: (a: AdminRegistrationDelta, b: AdminRegistrationDelta) => number;
}

/**
 * The auction's registrations, folded live, projected + sorted to the consumer's
 * shape. The consumer holds NO subscription / fold / catch-up / transport logic.
 */
export function useRegistrations<T = AdminRegistrationDelta>(
    opts: UseRegistrationsOptions<T> = {},
): T[] {
    const map = useRegistrationMap();
    const { select, sortBy } = opts;
    return useMemo(() => {
        const rows = [...map.values()];
        if (sortBy) rows.sort(sortBy);
        return select ? rows.map(select) : (rows as unknown as T[]);
    }, [map, select, sortBy]);
}

/**
 * Whether the registrations stream is established.
 *
 * Exposed because a consumer CANNOT infer this from `useRegistrations()` — an
 * auction with zero registrations is indistinguishable from a dead subscription
 * by count alone. `hasSeeded` is sticky, so a consumer can tell "still
 * connecting" (`!hasSeeded`) from "dropped after connecting"
 * (`hasSeeded && !isSubscribed`).
 */
export function useRegistrationsConnection(): RegistrationStateConnection {
    const store = useRegistrationStateStore();
    const subscribe = useCallback(
        (listener: () => void) => store.subscribe(listener),
        [store],
    );
    const getSnapshot = useCallback(() => store.getConnection(), [store]);
    return useSyncExternalStore(
        subscribe,
        getSnapshot,
        () => DISCONNECTED_REGISTRATION_STATE,
    );
}
